import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import dayjs from 'dayjs';
import ConfirmationModal from "../components/ConfirmationModal";
import BulkUpdateEventsModal from "../components/BulkUpdateEventsModal";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import { TableActionColumnHeader } from "../components/TableShell";
import GlobalTimeNavigatorBar from "../components/GlobalTimeNavigatorBar";
import OpenClosedFilterDropdown from "../components/OpenClosedFilterDropdown";
import ViewModeIconToggle from "../components/ViewModeIconToggle";
import EventScheduleCalendar from "../components/EventScheduleCalendar";
import EventProgramHostedByFilters from "../components/EventProgramHostedByFilters";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";
import { useEventProgramHostedByFilters } from "../shared/hooks/useEventProgramHostedByFilters";
import { getRegistrationStatusKey, REGISTRATION_STATUS_CONFIG } from '../shared/utils/eventRegistrationMetrics';
import { isEventExpired } from "../shared/utils/eventOpenClosedStatus";
import EventRegistrationQRCode from "../components/EventRegistrationQRCode";
import EventDescriptionPreview from "../components/EventDescriptionPreview";
import EventCardPriceMeta from "../components/EventCardPriceMeta";
import EventPriceBadge from "../components/EventPriceBadge";
import KidHoursTrackingStatus from "../components/KidHoursTrackingStatus";
import { buildEventsManagementPath, EVENT_MGMT_TAB_EVENTS, EVENT_MGMT_TAB_PARTICIPANTS } from '../shared/utils/eventManagementTabs';
import {
  AOP_ACTUAL_EVENTS_DRILL_SOURCE,
  AOP_ACTUAL_EVENTS_DRILL_SOURCE_SOP,
  getAopEventsDrillSourceLabel,
} from '../shared/utils/aopActualEventsDrill';
import { fetchCurrentAopProgramNames } from '../shared/api/currentAopByProgram';
import { isProgramNameInCurrentAop } from '../shared/utils/currentAopByProgram';
import { getEventKidHoursTotal, formatKidHours } from '../shared/utils/eventKidHours';
import { isValidYyyyMmDd, toYmdFromDateLike } from '../shared/utils/calendarYmd';
import { IconCalendar, IconLocation, IconEdit, IconDuplicate, IconReopen, IconParticipants, IconLeads, IconView, IconLink, iconBtnStyle, RowActionIcons } from '../shared/icons/IconLib';
import { useAuth } from "../contexts/AuthContext";
import { volunteerOpportunityRequestBody } from "../shared/api/volunteerOpportunityContext";
import { toCreateFromEventState } from "../shared/utils/volunteerOpportunityFromEvent";
import { buildVolunteerOpportunitiesPath } from "../shared/utils/volunteerOpportunityNavigation";
import { copyEventRegistrationUrl } from "../shared/api/eventRegistrationUrl";
import { getEventShareLinkLabels } from "../shared/utils/eventShareLinkLabels";
import { resolveEventBackgroundImageUrl } from '../shared/utils/eventBackgroundImage';
import { buildDuplicateLabel } from '../shared/utils/duplicateLabel';
import { intersectSelectedIdsWithRows } from '../shared/utils/entityListSelection';
import { isPlaceholderEvent, EVENT_PLACEHOLDER_TBA } from '../shared/utils/eventPlaceholder';
import {
  PAYMENT_MODE_INTEGRATED,
  PAYMENT_MODE_PAYMENT_LINK,
  getPaymentLinkUrl,
  getPaymentMode,
  isPaidEvent,
} from '../shared/utils/eventPaymentMode';
import { fetchTablePreferences, saveTablePreferences } from '../shared/api/tablePreferences';
import { PAGE_VIEW_PREFERENCE_KEYS } from '../shared/api/pagePreferences';
import { bulkUpdateEvents } from '../shared/api/events';
import { usePageViewPreferences } from '../shared/hooks/usePageViewPreferences';
import PageViewPreferenceSaveButton from '../components/PageViewPreferenceSaveButton';
import { hasSearchParamContext } from '../shared/utils/pageViewPreferenceState';
import {
  applyEventsListPagePreferenceState,
  collectEventsListPagePreferenceState,
  getDefaultEventsListPreferenceResetPatch,
} from '../shared/utils/eventsListPagePreference';

import { apiFetch } from "../shared/api/apiFetch";

const { getRepeatLabel } = require('../shared/utils/repeatOptions');
const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;
const EventDetailModal = require('../components/EventDetailModal').default;

function Events() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageVolunteerOpportunities = hasPermission && hasPermission("menu.volunteer_opportunities");
  const registrationStatusFromUrl = searchParams.get("registrationStatus") || "";
  const programFromUrl = searchParams.get("program") || "";
  const organizerFromUrl = (searchParams.get("organizer") || "").trim();
  const drillSourceFromUrl = searchParams.get("drillSource") || "";
  const startDateFromUrl = (searchParams.get("startDate") || "").trim();
  const endDateFromUrl = (searchParams.get("endDate") || "").trim();
  const hasValidDateRangeFromUrl =
    isValidYyyyMmDd(startDateFromUrl) &&
    isValidYyyyMmDd(endDateFromUrl) &&
    startDateFromUrl <= endDateFromUrl;
  const navViewFromUrl = (searchParams.get("navView") || "").trim().toLowerCase();
  const navDateFromUrl = (searchParams.get("navDate") || "").trim();
  const statusFromUrl = (searchParams.get("status") || "").trim().toLowerCase();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo.account_id;
  const user_id = userInfo?._id;
  const role = userInfo?.role;
  const volunteerSession = { account_id, user_id, role };
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const [volunteerOpportunities, setVolunteerOpportunities] = useState([]);

  const [currentObjs, setCurrentObjs] = useState([]);
  const {
    programs,
    filterProgramId,
    setFilterProgramId,
    filterOrganizerId,
    setFilterOrganizerId,
    filterProgramMembers,
  } = useEventProgramHostedByFilters({ account_id });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [bannerError, setBannerError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [programOrganizers, setProgramOrganizers] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => {
    if (statusFromUrl === "open" || statusFromUrl === "closed" || statusFromUrl === "expired" || statusFromUrl === "all") {
      return statusFromUrl;
    }
    return "open";
  }); // 'all' | 'open' | 'expired' | 'closed'
  const [detailEventId, setDetailEventId] = useState(null);
  const [copiedRegistrationEventId, setCopiedRegistrationEventId] = useState(null);
  /** null = not loaded yet (treat as Tracked until known); Set of program names in Current AOP */
  const [aopProgramNames, setAopProgramNames] = useState(null);
  const timeNav = useGlobalTimeNavigator({
    initialView: ["day", "week", "month", "future", "all"].includes(navViewFromUrl) ? navViewFromUrl : 'all',
    initialFocusDateYmd: navDateFromUrl || undefined,
  });

  // Apply URL-driven initial filters (dashboard / metrics drilldowns)
  useEffect(() => {
    // Default Open when URL has no status (or unrecognized). Explicit status=all|open|closed|expired still wins.
    if (statusFromUrl === "open" || statusFromUrl === "closed" || statusFromUrl === "expired" || statusFromUrl === "all") {
      setStatusFilter(statusFromUrl);
    } else {
      setStatusFilter("open");
    }
    if (["day", "week", "month", "future", "all"].includes(navViewFromUrl)) timeNav.setView(navViewFromUrl);
    if (navDateFromUrl) timeNav.setFocusDateYmd(navDateFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-driven initial filters
  }, []);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm",
    onConfirm: null,
  });
  /** Default table columns (Price and detail columns hidden; user can enable via column menu). */
  const EVENT_TABLE_DEFAULT_VISIBLE_IDS = [
    'name',
    'start_datetime',
    'repeat',
    'status',
    'program',
    'registered',
    'waitlisted',
    'cancelled',
    'capacity',
    'kid_hours',
  ];
  const [visibleEventColumns, setVisibleEventColumns] = useState(() => [...EVENT_TABLE_DEFAULT_VISIBLE_IDS]);
  const [eventColumnPrefsLoading, setEventColumnPrefsLoading] = useState(false);
  const [eventColumnPrefsSaving, setEventColumnPrefsSaving] = useState(false);
  const [eventColumnModalOpen, setEventColumnModalOpen] = useState(false);

  const EVENT_TABLE_COLUMNS = [
    { id: 'name', label: 'Name' },
    { id: 'start_datetime', label: 'Date' },
    { id: 'repeat', label: 'Repeat' },
    { id: 'status', label: 'Status' },
    { id: 'program', label: 'Program' },
    { id: 'price', label: 'Price ($)' },
    { id: 'registered', label: 'Registered' },
    { id: 'waitlisted', label: 'Waitlisted' },
    { id: 'cancelled', label: 'Cancelled' },
    { id: 'capacity', label: 'Capacity' },
    { id: 'kid_hours', label: 'Kid Hours' },
    { id: 'public_url', label: 'Public URL' },
    { id: 'visibility', label: 'Visibility' },
    { id: 'event_type', label: 'Event Type' },
    { id: 'audience_type', label: 'Audience Type' },
    { id: 'perfect_for', label: 'Perfect for' },
    { id: 'location', label: 'Location' },
    { id: 'is_placeholder', label: 'Placeholder' },
  ];
  const loadEvents = useCallback(() => {
    apiFetch(serverUrl + '/getEventsWithDetails', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }, [account_id, serverUrl]);

  const loadProgramOrganizers = useCallback(() => {
    apiFetch(serverUrl + '/getProgramOrganizers', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setProgramOrganizers(Array.isArray(data) ? data : []))
      .catch(() => setProgramOrganizers([]));
  }, [account_id, serverUrl]);

  useEffect(() => {
    loadEvents();
    loadProgramOrganizers();
  }, [loadEvents, loadProgramOrganizers]);

  useEffect(() => {
    const loadPrefs = async () => {
      if (!account_id || !userInfo?._id) return;
      try {
        setEventColumnPrefsLoading(true);
        const saved = await fetchTablePreferences(account_id, userInfo._id, 'events.table');
        if (Array.isArray(saved) && saved.length > 0) {
          const allowed = new Set(EVENT_TABLE_COLUMNS.map(c => c.id));
          const filtered = saved
            .map((id) => (id === 'impact_hours' ? 'kid_hours' : id))
            .filter(id => allowed.has(id));
          if (filtered.length > 0) {
            setVisibleEventColumns(filtered);
          }
        }
      } catch {
        // ignore and fall back to defaults
      } finally {
        setEventColumnPrefsLoading(false);
      }
    };
    loadPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once for prefs
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchCurrentAopProgramNames({ serverUrl, account_id })
      .then((names) => {
        if (!cancelled) setAopProgramNames(names);
      })
      .catch(() => {
        if (!cancelled) setAopProgramNames(null); // leave null → conservative Tracked until known
      });
    return () => {
      cancelled = true;
    };
  }, [serverUrl, account_id]);

  // Open event details modal when URL has ?detail=eventId (e.g. from /Events/123 redirect)
  useEffect(() => {
    const detailId = searchParams.get('detail');
    if (detailId) {
      setDetailEventId(detailId);
      const next = new URLSearchParams(searchParams);
      next.delete('detail');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Restore filter state when returning from edit (EventForm navigates back with eventsFilters)
  useEffect(() => {
    const filters = location.state?.eventsFilters;
    if (filters) {
      if (filters.searchTerm != null) setSearchTerm(filters.searchTerm);
      if (filters.statusFilter != null) {
        const v = String(filters.statusFilter || "").toLowerCase();
        setStatusFilter(v === "open" || v === "closed" || v === "expired" || v === "all" ? v : "open");
      }
      if (filters.filterProgramId != null) setFilterProgramId(filters.filterProgramId);
      if (filters.filterOrganizerId != null) setFilterOrganizerId(filters.filterOrganizerId);
      if (filters.timeNav && typeof filters.timeNav === "object") {
        const nextView = String(filters.timeNav.view || "").toLowerCase();
        if (["day", "week", "month"].includes(nextView)) timeNav.setView(nextView);
        const nextFocus = String(filters.timeNav.focusDateYmd || "").trim();
        if (nextFocus) timeNav.setFocusDateYmd(nextFocus);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.eventsFilters, location.pathname, navigate, timeNav, setFilterProgramId, setFilterOrganizerId]);

  useEffect(() => {
    if (location.state?.clearDrilldownFilters) {
      setFilterProgramId('');
      setStatusFilter('open');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.clearDrilldownFilters, location.pathname, navigate, setFilterProgramId]);

  useEffect(() => {
    if (!canManageVolunteerOpportunities || !account_id) {
      setVolunteerOpportunities([]);
      return;
    }
    apiFetch(serverUrl + "/listVolunteerOpportunities", {
      method: "POST",
      body: JSON.stringify(volunteerOpportunityRequestBody(volunteerSession)),
    })
      .then((resp) => resp.json())
      .then((data) => setVolunteerOpportunities(Array.isArray(data) ? data : []))
      .catch(() => setVolunteerOpportunities([]));
  }, [canManageVolunteerOpportunities, account_id, serverUrl, user_id, role]);

  const volunteerOppsByEventId = useMemo(() => {
    const map = new Map();
    for (const opp of volunteerOpportunities) {
      const eid = opp?.event_id != null ? String(opp.event_id).trim() : "";
      if (!eid) continue;
      if (!map.has(eid)) map.set(eid, []);
      map.get(eid).push(opp);
    }
    return map;
  }, [volunteerOpportunities]);

  function navigateToCreateVolunteerFromEvent(event) {
    navigate("/VolunteerOpportunities", {
      state: { createFromEvent: toCreateFromEventState(event) },
    });
  }

  function navigateToVolunteerOpportunitiesForEvent(eventId) {
    const linked = volunteerOppsByEventId.get(String(eventId)) || [];
    if (linked.length === 1) {
      navigate(buildVolunteerOpportunitiesPath({ detail: linked[0]._id }));
      return;
    }
    navigate(buildVolunteerOpportunitiesPath({ eventId }));
  }

  function renderVolunteerOpportunityActions(obj) {
    if (!canManageVolunteerOpportunities) return null;
    const linked = volunteerOppsByEventId.get(String(obj._id)) || [];
    return (
      <>
        {linked.length > 0 ? (
          <button
            type="button"
            onClick={() => navigateToVolunteerOpportunitiesForEvent(obj._id)}
            className="btn-link"
            style={iconBtnStyle}
            title={linked.length === 1 ? "View volunteer opportunity" : `View volunteer opportunities (${linked.length})`}
          >
            <IconView size={18} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => navigateToCreateVolunteerFromEvent(obj)}
          className="btn-link"
          style={iconBtnStyle}
          title="+ Volunteer Opportunity"
        >
          <IconLeads size={18} />
        </button>
      </>
    );
  }

  async function copyEventRegistrationLink(ev) {
    if (!ev?._id || !account_id || isPlaceholderEvent(ev)) return;
    const { copyErrorFallback } = getEventShareLinkLabels(ev);
    const origin = typeof window !== "undefined" ? window.location?.origin : "";
    try {
      await copyEventRegistrationUrl({
        eventId: ev._id,
        accountId: account_id,
        origin,
      });
    } catch (err) {
      setBannerError(err && err.message ? err.message : copyErrorFallback);
      return;
    }
    setCopiedRegistrationEventId(ev._id);
    window.setTimeout(() => {
      setCopiedRegistrationEventId((prev) => (prev === ev._id ? null : prev));
    }, 2000);
  }

  function renderCopyRegistrationLinkAction(obj) {
    if (!account_id || isPlaceholderEvent(obj)) return null;
    const { copyLabel, copiedLabel } = getEventShareLinkLabels(obj);
    const copied = copiedRegistrationEventId === obj._id;
    return (
      <button
        type="button"
        onClick={() => copyEventRegistrationLink(obj)}
        className="btn-link"
        style={iconBtnStyle}
        title={copied ? copiedLabel : copyLabel}
        aria-label={copied ? copiedLabel : copyLabel}
      >
        <IconLink size={18} />
      </button>
    );
  }

  function duplicateEvent(ev) {
    const dupLabel = buildDuplicateLabel(ev.name, { fallback: 'Event' });
    const isPh = Boolean(ev?.is_placeholder);
    apiFetch(serverUrl + '/createEvent', {
      method: 'POST',
        body: JSON.stringify({
          account_id,
        name: dupLabel,
        description: (ev.description || '').trim() || undefined,
        ...(ev.start_datetime ? { start_datetime: ev.start_datetime } : {}),
        ...(ev.end_datetime ? { end_datetime: ev.end_datetime } : {}),
        repeat_rule: isPh ? null : (ev.repeat_rule || null),
        location: (ev.location || '').trim() || undefined,
        program_id: (ev.program_id || '').trim() || undefined,
        event_organizer_id: (ev.event_organizer_id || '').trim() || undefined,
        visibility: ev.visibility === 'private' ? 'private' : 'public',
        is_placeholder: isPh,
        audience_type: ['Youth Only', 'Adult Only', 'All Ages'].includes(ev.audience_type) ? ev.audience_type : 'Youth Only',
        perfect_for: (ev.perfect_for != null ? String(ev.perfect_for) : '').trim() || undefined,
        event_type: isPh ? 'Free' : ((ev.event_type === 'Paid') ? 'Paid' : 'Free'),
        price_cents: !isPh && isPaidEvent(ev) ? ev.price_cents : undefined,
        ...(() => {
          if (isPh || !isPaidEvent(ev)) return {};
          const mode = getPaymentMode(ev);
          if (mode === PAYMENT_MODE_PAYMENT_LINK) {
            const url = getPaymentLinkUrl(ev);
            return url
              ? { payment_mode: PAYMENT_MODE_PAYMENT_LINK, payment_link_url: url }
              : {};
          }
          if (mode === PAYMENT_MODE_INTEGRATED) {
            return { payment_mode: PAYMENT_MODE_INTEGRATED };
          }
          return {};
        })(),
        max_capacity: ev.max_capacity != null ? ev.max_capacity : undefined,
        background_image_url: (ev.background_image_url || '').trim() || undefined,
      }),
    })
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) {
          setBannerError(text || 'Request failed');
          return;
        }
        let parsed = null;
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = null;
        }
        const newId = parsed && parsed._id != null ? String(parsed._id) : null;
        const openCreated = () => {
          setConfirmDialog({ open: false, title: '', message: '', mode: 'confirm', onConfirm: null });
          if (newId) setDetailEventId(newId);
        };
        setConfirmDialog({
          open: true,
          title: "Duplicate event",
          message: newId ? (
            <DuplicateSuccessMessage
              linkLabel={dupLabel.trim() || 'Duplicated event'}
              onOpenDetail={openCreated}
              footer={null}
            />
          ) : (
            parsed && typeof parsed.message === 'string' ? parsed.message : text
          ),
          mode: "alert",
          onConfirm: null,
        });
        loadEvents();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function toggleSelected(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleSelectAll() {
    const visibleIds = sortedObjs.map(o => o._id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  }

  function deleteSelected() {
    const visibleIds = Array.from(intersectSelectedIdsWithRows(new Set(selectedIds), sortedObjs));
    if (visibleIds.length === 0) return;
    setConfirmDialog({
      open: true,
      title: "Delete events",
      message: `Delete ${visibleIds.length} selected event(s)?`,
      onConfirm: () => {
        apiFetch(serverUrl + '/deleteEvents', {
          method: 'POST',
          body: JSON.stringify({ account_id, ids: visibleIds }),
        })
          .then(r => r.text())
          .then(text => {
            setConfirmDialog({
              open: true,
              title: "Delete events",
              message: text,
              mode: "alert",
              onConfirm: null,
            });
            setSelectedIds([]);
            loadEvents();
          })
          .catch(err => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function openBulkUpdate() {
    if (intersectSelectedIdsWithRows(new Set(selectedIds), sortedObjs).size === 0) {
      setConfirmDialog({
        open: true,
        title: "Bulk update",
        message: "Please select at least one event to update",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setBulkUpdateOpen(true);
  }

  async function applyBulkUpdate(fields) {
    const visibleIds = Array.from(intersectSelectedIdsWithRows(new Set(selectedIds), sortedObjs));
    if (visibleIds.length === 0) {
      setBulkUpdateOpen(false);
      return;
    }
    const ids = visibleIds;
    const payload = {
      account_id,
      _ids: ids,
      viewer_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...fields,
    };
    try {
      setBulkUpdating(true);
      const res = await bulkUpdateEvents(payload);
      const matched = res && typeof res.matched === "number" ? res.matched : ids.length;
      const modified = res && typeof res.modified === "number" ? res.modified : 0;
      setConfirmDialog({
        open: true,
        title: "Bulk update completed",
        mode: "alert",
        onConfirm: null,
        message: `Updated ${modified} event(s) (matched ${matched}).`,
      });
      setSelectedIds([]);
      setBulkUpdateOpen(false);
      loadEvents();
    } catch (err) {
      setConfirmDialog({
        open: true,
        title: "Bulk update failed",
        mode: "alert",
        onConfirm: null,
        message: err && err.message ? err.message : "Bulk update failed",
      });
    } finally {
      setBulkUpdating(false);
    }
  }

  const programById = Object.fromEntries((programs || []).map(g => [g._id, g]));
  const memberById = {};

  const todayForPhase = dayjs().startOf("day");
  const isExpired = (ev) => isEventExpired(ev, todayForPhase);

  const getEventProgramName = useCallback(
    (ev) => {
      if (!ev) return "";
      if (ev.program_name != null && String(ev.program_name).trim() !== "") {
        return String(ev.program_name).trim();
      }
      const pid = ev.program_id;
      if (pid && programById[pid]) return (programById[pid].name || "").trim();
      return "";
    },
    [programById]
  );

  const isEventKidHoursTracked = useCallback(
    (ev) => isProgramNameInCurrentAop(getEventProgramName(ev), aopProgramNames),
    [getEventProgramName, aopProgramNames]
  );

  function getFilteredAndSortedData() {
    const bounds = timeNav.bounds;
    const rangeStart = bounds.startInclusive ? dayjs(bounds.startInclusive) : null;
    const rangeEnd = bounds.endInclusive ? dayjs(bounds.endInclusive) : null;
    let filtered = (currentObjs || []).filter((obj) => {
      const start = obj.start_datetime ? dayjs(obj.start_datetime) : null;
      const end = obj.end_datetime ? dayjs(obj.end_datetime) : null;
      const a = start && start.isValid() ? start : (end && end.isValid() ? end : null);
      const b = end && end.isValid() ? end : (start && start.isValid() ? start : null);
      if (!a || !b) {
        // Undated placeholder events have no schedule yet — still show in management
        // (list/table). Calendar day cells only include dated events.
        if (!isPlaceholderEvent(obj)) return false;
      } else if (timeNav.view === "future") {
        // Future view: long-term horizon should mean "starts on/after rangeStart" (not overlap).
        if (rangeStart && a.isBefore(rangeStart)) return false;
      } else {
        // Month/week/day/all: overlap is correct (spanning events count in the range)
        if (rangeStart && b.isBefore(rangeStart)) return false;
        if (rangeEnd && a.isAfter(rangeEnd)) return false;
      }
      const status = (obj.status || "Open").toLowerCase();
      const expired = isExpired(obj);
      // Open = still open and not past end date; Expired = open but past end; Closed = closed.
      if (statusFilter === "open" && (status !== "open" || expired)) return false;
      if (statusFilter === "expired" && !expired) return false;
      if (statusFilter === "closed" && status !== "closed") return false;
      if (hasValidDateRangeFromUrl) {
        const ymd = toYmdFromDateLike(obj.start_datetime);
        if (!ymd || ymd < startDateFromUrl || ymd > endDateFromUrl) return false;
      }
      if (
        drillSourceFromUrl === AOP_ACTUAL_EVENTS_DRILL_SOURCE
        || drillSourceFromUrl === AOP_ACTUAL_EVENTS_DRILL_SOURCE_SOP
      ) {
        if (!(getEventKidHoursTotal(obj) > 0)) return false;
      }
      return true;
    });
    if (registrationStatusFromUrl) {
      if (registrationStatusFromUrl === 'no_cap') {
        filtered = filtered.filter(obj => getRegistrationStatusKey(obj) === null);
      } else {
        filtered = filtered.filter(obj => getRegistrationStatusKey(obj) === registrationStatusFromUrl);
      }
    }
    const effectiveProgramId = (programFromUrl && programFromUrl !== '__none__') ? programFromUrl : ((programFromUrl === '__none__') ? '__none__' : (filterProgramId || '').trim());
    if (effectiveProgramId) {
      if (effectiveProgramId === '__none__') {
        filtered = filtered.filter(obj => !(obj.program_id || '').trim());
      } else {
        filtered = filtered.filter(obj => (obj.program_id || '') === effectiveProgramId);
      }
    }
    const effectiveOrganizerId = (organizerFromUrl || filterOrganizerId || "").trim();
    if (effectiveOrganizerId) {
      filtered = filtered.filter(obj => (obj.event_organizer_id || '') === effectiveOrganizerId);
    }
    filtered = filtered.filter(obj => {
      const searchLower = (searchTerm || '').trim().toLowerCase();
      if (!searchLower) return true;
      const groupName = (obj.program_name != null && obj.program_name !== '') ? obj.program_name : ((obj.program_id && programById[obj.program_id]) ? programById[obj.program_id].name : '');
      const organizer = (obj.organizer_name != null && obj.organizer_name !== '')
        ? obj.organizer_name
        : (obj.event_organizer_id && memberById[obj.event_organizer_id])
          ? `${(memberById[obj.event_organizer_id].first_name || '')} ${(memberById[obj.event_organizer_id].last_name || '')}`.trim() : '';
      const statusStr = (obj.status || 'Open').toLowerCase();
      const perfectFor = (obj.perfect_for || '').trim().toLowerCase();
      return (
        (obj.name && obj.name.toLowerCase().includes(searchLower)) ||
        (obj.description && obj.description.toLowerCase().includes(searchLower)) ||
        (obj.location && obj.location.toLowerCase().includes(searchLower)) ||
        (groupName && groupName.toLowerCase().includes(searchLower)) ||
        (organizer && organizer.toLowerCase().includes(searchLower)) ||
        (statusStr && statusStr.includes(searchLower)) ||
        (perfectFor && perfectFor.includes(searchLower))
      );
    });
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === 'start_datetime' || sortConfig.key === 'end_datetime') {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }
        if (sortConfig.key === 'program_id') {
          aVal = (a.program_name != null && a.program_name !== '') ? a.program_name : ((aVal && programById[aVal]) ? programById[aVal].name : '');
          bVal = (b.program_name != null && b.program_name !== '') ? b.program_name : ((bVal && programById[bVal]) ? programById[bVal].name : '');
        }
        if (sortConfig.key === 'participant_count') {
          aVal = a.participant_count ?? 0;
          bVal = b.participant_count ?? 0;
        }
        if (sortConfig.key === 'registered_count') {
          aVal = a.registered_count ?? 0;
          bVal = b.registered_count ?? 0;
        }
        if (sortConfig.key === 'waitlisted_count') {
          aVal = a.waitlisted_count ?? 0;
          bVal = b.waitlisted_count ?? 0;
        }
        if (sortConfig.key === 'cancelled_count') {
          aVal = a.cancelled_count ?? 0;
          bVal = b.cancelled_count ?? 0;
        }
        if (sortConfig.key === 'kid_hours') {
          aVal = getEventKidHoursTotal(a);
          bVal = getEventKidHoursTotal(b);
        }
        if (sortConfig.key === 'max_capacity') {
          aVal = a.max_capacity != null ? Number(a.max_capacity) : -1;
          bVal = b.max_capacity != null ? Number(b.max_capacity) : -1;
        }
        if (sortConfig.key === 'price_cents') {
          aVal = a.price_cents != null ? Number(a.price_cents) : -1;
          bVal = b.price_cents != null ? Number(b.price_cents) : -1;
        }
        if (sortConfig.key === 'slug') {
          aVal = (a.slug || '').toLowerCase();
          bVal = (b.slug || '').toLowerCase();
        }
        if (sortConfig.key === 'visibility') {
          aVal = (a.visibility === 'private' ? 'private' : 'public');
          bVal = (b.visibility === 'private' ? 'private' : 'public');
        }
        if (sortConfig.key === 'event_type') {
          aVal = isPlaceholderEvent(a) ? 'free' : ((a.event_type === 'Paid') ? 'paid' : 'free');
          bVal = isPlaceholderEvent(b) ? 'free' : ((b.event_type === 'Paid') ? 'paid' : 'free');
        }
        if (sortConfig.key === 'audience_type') {
          aVal = ['Youth Only', 'Adult Only', 'All Ages'].includes(a.audience_type) ? a.audience_type : 'Youth Only';
          bVal = ['Youth Only', 'Adult Only', 'All Ages'].includes(b.audience_type) ? b.audience_type : 'Youth Only';
        }
        if (sortConfig.key === 'perfect_for') {
          aVal = (a.perfect_for != null ? String(a.perfect_for) : '').trim().toLowerCase();
          bVal = (b.perfect_for != null ? String(b.perfect_for) : '').trim().toLowerCase();
        }
        if (sortConfig.key === 'location') {
          aVal = (a.location || '').trim().toLowerCase();
          bVal = (b.location || '').trim().toLowerCase();
        }
        if (sortConfig.key === 'is_placeholder') {
          aVal = isPlaceholderEvent(a) ? 1 : 0;
          bVal = isPlaceholderEvent(b) ? 1 : 0;
        }
        if (sortConfig.key === 'status') {
          const statusOrder = (o) => (o.status || "").toLowerCase() === "closed" ? 0 : isExpired(o) ? 1 : 2;
          aVal = statusOrder(a);
          bVal = statusOrder(b);
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function handleSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  }

  const sortedObjs = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(new Set(selectedIds), sortedObjs);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedClosedIds = useMemo(() => {
    const idSet = new Set(selectedIds);
    return sortedObjs
      .filter((o) => idSet.has(o._id) && (o.status || "Open") === "Closed")
      .map((o) => o._id);
  }, [selectedIds, sortedObjs]);
  const returnToHere = `${location.pathname}${location.search || ""}`;

  async function reopenOneEvent(ev) {
    try {
      const response = await apiFetch(serverUrl + '/reopenEvent', {
        method: 'POST',
        body: JSON.stringify({ account_id, _id: ev._id }),
      });
      const text = await response.text();
      if (response.ok) {
        loadEvents();
      } else {
        setBannerError(text || 'Failed to reopen event');
      }
    } catch (err) {
      setBannerError(getMessageForFetchError(err, 'Failed to reopen event'));
    }
  }

  function reopenSelected() {
    if (selectedClosedIds.length === 0) {
      setConfirmDialog({
        open: true,
        title: "Reopen events",
        message: "Select one or more closed events to reopen.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Reopen events",
      message: `Reopen ${selectedClosedIds.length} selected closed event(s)?`,
      onConfirm: () => {
        apiFetch(serverUrl + '/reopenEvents', {
          method: 'POST',
          body: JSON.stringify({ account_id, ids: selectedClosedIds }),
        })
          .then((r) => r.text())
          .then((text) => {
            setConfirmDialog({
              open: true,
              title: "Reopen events",
              message: text,
              mode: "alert",
              onConfirm: null,
            });
            setSelectedIds([]);
            loadEvents();
          })
          .catch((err) => setBannerError(getMessageForFetchError(err, 'Failed to reopen events')));
      },
    });
  }

  const shouldSkipEventsPagePreferenceLoad = useCallback(
    () =>
      hasSearchParamContext(searchParams, [
        "registrationStatus",
        "program",
        "organizer",
        "drillSource",
        "startDate",
        "endDate",
        "status",
        "navView",
        "navDate",
        "detail",
      ]),
    [searchParams]
  );

  const collectEventsPageViewState = useCallback(
    () =>
      collectEventsListPagePreferenceState({
        searchTerm,
        statusFilter,
        viewMode,
        navView: timeNav.view,
        navDate: timeNav.focusDateYmd,
        filterProgramId,
        filterOrganizerId,
      }),
    [
      searchTerm,
      statusFilter,
      viewMode,
      timeNav.view,
      timeNav.focusDateYmd,
      filterProgramId,
      filterOrganizerId,
    ]
  );

  const applyEventsPageViewState = useCallback(
    (state) => {
      applyEventsListPagePreferenceState(state, {
        setSearchTerm,
        setStatusFilter,
        setViewMode,
        setNavView: timeNav.setView,
        setNavDate: timeNav.setFocusDateYmd,
        setFilterProgramId,
        setFilterOrganizerId,
      });
    },
    [timeNav.setView, timeNav.setFocusDateYmd, setFilterProgramId, setFilterOrganizerId]
  );

  const clearEventsPageFiltersAfterDefaultCleared = useCallback(() => {
    const patch = getDefaultEventsListPreferenceResetPatch();
    setSearchTerm(patch.searchTerm);
    setStatusFilter(patch.statusFilter);
    setViewMode(patch.viewMode);
    timeNav.setView(patch.navView);
    timeNav.setFocusDateYmd(patch.navDate);
    setFilterProgramId(patch.filterProgramId);
    setFilterOrganizerId(patch.filterOrganizerId);
  }, [timeNav, setFilterProgramId, setFilterOrganizerId]);

  const confirmClearEventsDefaultView = useCallback(
    (proceed) => {
      setConfirmDialog({
        open: true,
        title: "Reset default view",
        message: "Clear your saved Events default view and restore the standard filters?",
        mode: "confirm",
        onConfirm: () => {
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
          if (typeof proceed === "function") proceed();
        },
      });
    },
    []
  );

  const eventsPageViewPreferenceHookConfig = useMemo(
    () => ({
      pageKey: PAGE_VIEW_PREFERENCE_KEYS.eventsList,
      accountId: account_id,
      userId: userInfo?._id,
      enabled: Boolean(account_id && userInfo?._id),
      shouldSkipLoad: shouldSkipEventsPagePreferenceLoad,
      collectState: collectEventsPageViewState,
      applyState: applyEventsPageViewState,
    }),
    [
      account_id,
      userInfo?._id,
      shouldSkipEventsPagePreferenceLoad,
      collectEventsPageViewState,
      applyEventsPageViewState,
    ]
  );

  const eventsPageViewPreferences = usePageViewPreferences(eventsPageViewPreferenceHookConfig);

  return (
    <div className="page-content">
        <div className="page-top">
        {(registrationStatusFromUrl || programFromUrl || drillSourceFromUrl || hasValidDateRangeFromUrl) && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#495057', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>Filter from {getAopEventsDrillSourceLabel(drillSourceFromUrl)}:</span>
            {registrationStatusFromUrl && <span style={{ padding: '2px 6px', background: '#e9ecef', borderRadius: '4px' }}>Registration: {registrationStatusFromUrl === 'no_cap' ? 'No cap' : registrationStatusFromUrl.replace(/_/g, ' ')}</span>}
            {programFromUrl && <span style={{ padding: '2px 6px', background: '#e9ecef', borderRadius: '4px' }}>Program: {programFromUrl === '__none__' ? 'No program' : (programs.find(p => String(p._id) === programFromUrl)?.name || programFromUrl)}</span>}
            {hasValidDateRangeFromUrl && <span style={{ padding: '2px 6px', background: '#e9ecef', borderRadius: '4px' }}>Date range: {startDateFromUrl} – {endDateFromUrl}</span>}
            <button type="button" onClick={() => navigate(buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS }), { state: { clearDrilldownFilters: true } })} style={{ padding: '2px 8px', fontSize: '12px', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#0d6efd' }}>Clear filter</button>
          </div>
        )}
        </div>
      <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />

      <ColumnCustomizerModal
        open={eventColumnModalOpen}
        onClose={() => setEventColumnModalOpen(false)}
        allColumns={EVENT_TABLE_COLUMNS}
        visibleIds={visibleEventColumns}
        onChangeVisibleIds={setVisibleEventColumns}
        saving={eventColumnPrefsSaving}
        onReset={async () => {
          const defaultIds = [...EVENT_TABLE_DEFAULT_VISIBLE_IDS];
          setVisibleEventColumns(defaultIds);
          if (!account_id || !userInfo?._id) {
            setEventColumnModalOpen(false);
            return;
          }
          try {
            setEventColumnPrefsSaving(true);
            await saveTablePreferences(account_id, userInfo._id, 'events.table', defaultIds);
          } catch (err) {
            setConfirmDialog({
              open: true,
              title: 'Event columns',
              message: err && err.message ? err.message : 'Failed to reset column preferences',
              mode: 'alert',
              onConfirm: null,
            });
          } finally {
            setEventColumnPrefsSaving(false);
          }
          setEventColumnModalOpen(false);
        }}
        onSave={async (ids) => {
          if (!account_id || !userInfo?._id) {
            setVisibleEventColumns(ids);
            setEventColumnModalOpen(false);
            return;
          }
          try {
            setEventColumnPrefsSaving(true);
            await saveTablePreferences(account_id, userInfo._id, 'events.table', ids);
            setVisibleEventColumns(ids);
            setEventColumnModalOpen(false);
          } catch (err) {
            // Basic surface; reuse banner if desired later
            setConfirmDialog({
              open: true,
              title: 'Event columns',
              message: err && err.message ? err.message : 'Failed to save column preferences',
              mode: 'alert',
              onConfirm: null,
            });
          } finally {
            setEventColumnPrefsSaving(false);
          }
        }}
      />

      <BulkUpdateEventsModal
        open={bulkUpdateOpen}
        onClose={() => {
          if (bulkUpdating) return;
          setBulkUpdateOpen(false);
        }}
        selectedCount={selectedVisibleCount}
        programs={programs}
        programOrganizers={programOrganizers}
        loading={bulkUpdating}
        onApply={applyBulkUpdate}
      />

      <ConfirmationModal
        open={confirmDialog.open}
        title={confirmDialog.title || (confirmDialog.mode === "alert" ? "Message" : "Confirm")}
        message={confirmDialog.message}
        mode={confirmDialog.mode || "confirm"}
        onConfirm={() => {
          const fn = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
          if (fn) fn();
        }}
        onCancel={() =>
          setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
        }
      />

      {/* Filters row only */}
      <div className="toolbar">
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search name, description, location, program, organizer, status…" className="input input-search-260" />
        <button type="button" onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Clear</button>
        <span className="toolbar-divider" />
        <OpenClosedFilterDropdown value={statusFilter} onChange={setStatusFilter} label="Status" includeExpired />
        <span className="toolbar-divider" />
        <EventProgramHostedByFilters
          programs={programs}
          filterProgramId={filterProgramId}
          onProgramChange={setFilterProgramId}
          filterOrganizerId={filterOrganizerId}
          onOrganizerChange={setFilterOrganizerId}
          filterProgramMembers={filterProgramMembers}
        />
        <span className="toolbar-divider" />
      </div>
      <div className="toolbar">
        <button type="button" onClick={() => navigate('/Events/new')} className="btn btn-primary btn-md">+ New</button>
        {viewMode === 'table' && (
          <>
            <button
              type="button"
              onClick={openBulkUpdate}
              disabled={selectedVisibleCount === 0 || bulkUpdating}
              className="btn btn-md btn-muted"
              title={selectedVisibleCount === 0 ? 'Select events to update' : `Update ${selectedVisibleCount} selected`}
            >
              Update {selectedVisibleCount > 0 ? `(${selectedVisibleCount})` : ''}
            </button>
            <button
              type="button"
              onClick={reopenSelected}
              disabled={selectedClosedIds.length === 0}
              className="btn btn-md btn-muted"
              title={selectedClosedIds.length === 0 ? 'Select closed events to reopen' : `Reopen ${selectedClosedIds.length} selected closed event(s)`}
            >
              Reopen {selectedClosedIds.length > 0 ? `(${selectedClosedIds.length})` : ''}
            </button>
            <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0} className="btn btn-danger btn-md" title={selectedVisibleCount === 0 ? 'Select events to delete' : `Delete ${selectedVisibleCount} selected`}>Delete {selectedVisibleCount > 0 ? `(${selectedVisibleCount})` : ''}</button>
          </>
        )}
        <span className="toolbar-divider" />
        <ViewModeIconToggle
          value={viewMode}
          onChange={(nextViewMode) => {
            setViewMode(nextViewMode);
            if (nextViewMode === 'calendar' && !['day', 'week', 'month'].includes(timeNav.view)) {
              timeNav.setView('month');
            }
          }}
          modes={["calendar", "table", "card"]}
          ariaLabel="View"
        />
        <PageViewPreferenceSaveButton
          {...eventsPageViewPreferenceHookConfig}
          preferencesControl={eventsPageViewPreferences}
          onAfterClearLocalState={clearEventsPageFiltersAfterDefaultCleared}
          onClearConfirm={confirmClearEventsDefaultView}
          onError={(message) =>
            setConfirmDialog({
              open: true,
              title: "Default view",
              message,
              mode: "alert",
              onConfirm: null,
            })
          }
        />
        <span className="toolbar-divider" />
        <GlobalTimeNavigatorBar
          variant="inline"
          title={timeNav.title}
          view={timeNav.view}
          onViewChange={timeNav.setView}
          onToday={timeNav.goToday}
          onPrev={timeNav.movePrev}
          onNext={timeNav.moveNext}
          viewOptions={viewMode === 'calendar' ? ['day', 'week', 'month'] : undefined}
        />
      </div>

      <div className="table-scroll">
        {viewMode === 'card' && (
          sortedObjs.length === 0 ? (
            <p className="empty-message">No events. Add one above.</p>
          ) : (
          <div className="cards-wrapper cards-wrapper--events">
            {sortedObjs.map((obj) => {
              const organizerDisplay =
                (obj.organizer_name != null && String(obj.organizer_name).trim() !== "")
                  ? String(obj.organizer_name).trim()
                  : (obj.event_organizer_id && memberById[obj.event_organizer_id])
                    ? [memberById[obj.event_organizer_id].first_name, memberById[obj.event_organizer_id].last_name].filter(Boolean).join(" ").trim()
                    : "";
              const hostedBy = organizerDisplay;
              const heroBgUrl = resolveEventBackgroundImageUrl(obj.background_image_url);
              const placeholder = isPlaceholderEvent(obj);
              return (
                <article key={obj._id} className="card EventCalendar-card--hero">
                  <div
                    className="EventCalendar-card-hero-banner"
                    style={{
                      backgroundImage: `url(${heroBgUrl})`,
                    }}
                    aria-hidden={true}
                  />
                  <div className="card-body">
                    <div className="card-main">
                      <div className="card-main-details">
                        <div className="card-date">
                          <IconCalendar />
                          {placeholder && !obj.start_datetime
                            ? EVENT_PLACEHOLDER_TBA
                            : obj.start_datetime
                              ? dayjs(obj.start_datetime).format("dddd, MMMM D, YYYY")
                              : "—"}
                        </div>
                        {obj.start_datetime && obj.end_datetime && (
                          <div className="card-time">
                            {dayjs(obj.start_datetime).format("h:mm A")} – {dayjs(obj.end_datetime).format("h:mm A")}
                          </div>
                        )}
                        <h3 className="card-title">
                          <button
                            type="button"
                            onClick={() => setDetailEventId(obj._id)}
                            style={{
                              padding: 0,
                              margin: 0,
                              border: "none",
                              background: "none",
                              color: "inherit",
                              font: "inherit",
                              cursor: "pointer",
                              textAlign: "left",
                              width: "100%",
                            }}
                            title="View event details"
                          >
                            {obj.name || "Event"}
                          </button>
                        </h3>
                        {hostedBy ? (
                          <div className="card-meta">
                            <span className="card-meta-label">Hosted By:</span>
                            <span>{hostedBy}</span>
                          </div>
                        ) : null}
                        {(obj.audience_type || "").trim() ? (
                          <div className="card-meta">
                            <span className="card-meta-label">Audience Type:</span>
                            <span>{(obj.audience_type || "").trim()}</span>
                          </div>
                        ) : null}
                        {(obj.perfect_for || "").trim() ? (
                          <div className="card-meta">
                            <span className="card-meta-label">Perfect For:</span>
                            <span>{(obj.perfect_for || "").trim()}</span>
                          </div>
                        ) : null}
                        <EventCardPriceMeta event={obj} />
                        <KidHoursTrackingStatus tracked={isEventKidHoursTracked(obj)} />
                        {(obj.location || placeholder) ? (
                          <div className="card-location" title={obj.location || EVENT_PLACEHOLDER_TBA}>
                            <IconLocation />
                            <span>{(obj.location || "").trim() || EVENT_PLACEHOLDER_TBA}</span>
                          </div>
                        ) : null}
                        <EventDescriptionPreview
                          description={obj.description}
                          dialogTitle={obj.name ? `${obj.name} — Description` : "Description"}
                        />
                      </div>
                      {account_id && !placeholder ? (
                        <div className="card-main-qr">
                          <EventRegistrationQRCode
                            event={obj}
                            accountId={account_id}
                            serverUrl={serverUrl}
                            size={120}
                            className="card-qr-wrap"
                          />
                        </div>
                      ) : null}
                    </div>
                    <div
                      className="card-actions"
                      style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}
                    >
                      <span style={{ display: "inline-flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                      {isExpired(obj) ? (
                        <span className="badge" style={{ background: "#fef2f2", color: "#dc2626" }}>Expired</span>
                      ) : (obj.status || "Open").toLowerCase() === "closed" ? (
                        <span className="badge" style={{ background: "#f3f4f6", color: "#374151" }}>Closed</span>
                      ) : (
                        <span className="badge" style={{ background: "#f0fdf4", color: "#16a34a" }}>Open</span>
                      )}
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          gap: "4px",
                          flexWrap: "nowrap",
                          fontSize: "0.72rem",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {renderVolunteerOpportunityActions(obj)}
                        {renderCopyRegistrationLinkAction(obj)}
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/Events/${obj._id}/edit`, {
                              state: {
                                event: obj,
                                eventsFilters: {
                                  searchTerm,
                                  statusFilter,
                                  filterProgramId,
                                  filterOrganizerId,
                                  timeNav: { view: timeNav.view, focusDateYmd: timeNav.focusDateYmd },
                                },
                                returnTo: returnToHere,
                              },
                            })
                          }
                          className="btn-link"
                          style={iconBtnStyle}
                          title="Edit"
                        >
                          <IconEdit size={18} />
                        </button>
                        {(obj.status || "Open") === "Closed" && (
                          <button type="button" onClick={() => reopenOneEvent(obj)} className="btn-link-muted" style={iconBtnStyle} title="Reopen">
                            <IconReopen size={18} />
                          </button>
                        )}
                        <button type="button" onClick={() => duplicateEvent(obj)} className="btn-link" style={iconBtnStyle} title="Duplicate">
                          <IconDuplicate size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(buildEventsManagementPath({ tab: EVENT_MGMT_TAB_PARTICIPANTS, eventId: obj._id }))}
                          className="btn-link"
                          title="View participants"
                          style={iconBtnStyle}
                        >
                          <IconParticipants size={18} />
                        </button>
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          )
        )}

        {viewMode === 'table' && (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 40, minWidth: 40 }}>
                      <input type="checkbox" checked={sortedObjs.length > 0 && sortedObjs.every(o => selectedIds.includes(o._id))} onChange={toggleSelectAll} title="Select all" />
                    </th>
                    {visibleEventColumns.map((colId) => {
                      switch (colId) {
                        case 'name':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('name')}>
                              Name
                            </th>
                          );
                        case 'start_datetime':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('start_datetime')}>
                              Date
                            </th>
                          );
                        case 'repeat':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('repeat')}>
                              Repeat
                            </th>
                          );
                        case 'status':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('status')}>
                              Status
                            </th>
                          );
                        case 'program':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('program_id')}>
                              Program
                            </th>
                          );
                        case 'price':
                          return (
                            <th key={colId} className="sortable table-cell-ellipsis-sm">
                              Price ($)
                            </th>
                          );
                        case 'registered':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('registered_count')}>
                              Registered
                            </th>
                          );
                        case 'waitlisted':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('waitlisted_count')}>
                              Waitlisted
                            </th>
                          );
                        case 'cancelled':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('cancelled_count')}>
                              Cancelled
                            </th>
                          );
                        case 'capacity':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('max_capacity')}>
                              Capacity
                            </th>
                          );
                        case 'kid_hours':
                          return (
                            <th key={colId} className="sortable text-center" onClick={() => handleSort('kid_hours')}>
                              Kid Hours
                            </th>
                          );
                        case 'public_url':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('slug')}>
                              Public URL
                            </th>
                          );
                        case 'visibility':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('visibility')}>
                              Visibility
                            </th>
                          );
                        case 'event_type':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('event_type')}>
                              Event Type
                            </th>
                          );
                        case 'audience_type':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('audience_type')}>
                              Audience Type
                            </th>
                          );
                        case 'perfect_for':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('perfect_for')}>
                              Perfect for
                            </th>
                          );
                        case 'location':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('location')}>
                              Location
                            </th>
                          );
                        case 'is_placeholder':
                          return (
                            <th key={colId} className="sortable" onClick={() => handleSort('is_placeholder')}>
                              Placeholder
                            </th>
                          );
                        default:
                          return null;
                      }
                    })}
                    <TableActionColumnHeader
                      align="left"
                      onCustomizeColumns={() => setEventColumnModalOpen(true)}
                      customizeDisabled={eventColumnPrefsLoading}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedObjs.map(obj => (
                    <tr key={obj._id}>
                      <td className="text-center">
                        <input type="checkbox" checked={selectedIds.includes(obj._id)} onChange={() => toggleSelected(obj._id)} />
                      </td>
                      {visibleEventColumns.map((colId) => {
                        switch (colId) {
                          case 'name':
                            return (
                              <td key={colId}>
                                <button type="button" onClick={() => setDetailEventId(obj._id)} className="btn-link" title="View event details">
                                  {obj.name}
                                </button>
                              </td>
                            );
                          case 'start_datetime':
                            return (
                              <td key={colId}>
                                {isPlaceholderEvent(obj) && !obj.start_datetime
                                  ? EVENT_PLACEHOLDER_TBA
                                  : obj.start_datetime
                                    ? dayjs(obj.start_datetime).format('MM/DD/YYYY')
                                    : '—'}
                              </td>
                            );
                          case 'repeat':
                            return (
                              <td key={colId}>{getRepeatLabel(obj.repeat_rule)}</td>
                            );
                          case 'status':
                            return (
                              <td key={colId}>
                                {isExpired(obj) ? (
                                  <span className="badge" style={{ background: "#fef2f2", color: "#dc2626" }}>Expired</span>
                                ) : (obj.status || "Open").toLowerCase() === "closed" ? (
                                  <span className="badge" style={{ background: "#f3f4f6", color: "#374151" }}>Closed</span>
                                ) : (
                                  <span className="badge" style={{ background: "#f0fdf4", color: "#16a34a" }}>Open</span>
                                )}
                              </td>
                            );
                          case 'program':
                            return (
                              <td key={colId}>{(obj.program_name != null && obj.program_name !== '') ? obj.program_name : (obj.program_id && programById[obj.program_id]) ? programById[obj.program_id].name : '—'}</td>
                            );
                          case 'price':
                            return (
                              <td key={colId}><EventPriceBadge event={obj} /></td>
                            );
                          case 'registered':
                            return (
                              <td key={colId}>
                                <button type="button" onClick={() => navigate(buildEventsManagementPath({ tab: EVENT_MGMT_TAB_PARTICIPANTS, eventId: obj._id }))} className="btn-link" title="View participants">
                                  {obj.registered_count ?? 0}
                                </button>
                              </td>
                            );
                          case 'waitlisted':
                            return (
                              <td key={colId}>{obj.waitlisted_count ?? 0}</td>
                            );
                          case 'cancelled':
                            return (
                              <td key={colId}>{obj.cancelled_count ?? 0}</td>
                            );
                          case 'capacity':
                            return (
                              <td key={colId}>
                                {(() => {
                                  const key = getRegistrationStatusKey(obj) || 'no_cap';
                                  const cfg = REGISTRATION_STATUS_CONFIG[key] || REGISTRATION_STATUS_CONFIG.no_cap || { label: key, color: '#e9ecef' };
                                  return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                      {obj.max_capacity != null ? obj.max_capacity : '—'}
                                      {cfg && (
                                        <span
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '1px 8px',
                                            borderRadius: '9999px',
                                            backgroundColor: cfg.color,
                                            fontSize: '11px',
                                            color: '#0f172a',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {cfg.label}
                                        </span>
                                      )}
                                    </span>
                                  );
                                })()}
                              </td>
                            );
                          case 'kid_hours':
                            return (
                              <td key={colId} className="text-center">
                                {formatKidHours(getEventKidHoursTotal(obj))}
                              </td>
                            );
                          case 'public_url':
                            return (
                              <td key={colId}>{(obj.slug || '').trim() || '—'}</td>
                            );
                          case 'visibility':
                            return (
                              <td key={colId}>{obj.visibility === 'private' ? 'Private' : 'Public'}</td>
                            );
                          case 'event_type':
                            return (
                              <td key={colId}>
                                {isPlaceholderEvent(obj) ? 'Free' : (obj.event_type === 'Paid' ? 'Paid' : 'Free')}
                              </td>
                            );
                          case 'audience_type':
                            return (
                              <td key={colId}>
                                {['Youth Only', 'Adult Only', 'All Ages'].includes(obj.audience_type) ? obj.audience_type : 'Youth Only'}
                              </td>
                            );
                          case 'perfect_for':
                            return (
                              <td key={colId}>{(obj.perfect_for != null ? String(obj.perfect_for) : '').trim() || '—'}</td>
                            );
                          case 'location':
                            return (
                              <td key={colId}>
                                {isPlaceholderEvent(obj) && !(obj.location || '').trim()
                                  ? EVENT_PLACEHOLDER_TBA
                                  : (obj.location || '').trim() || '—'}
                              </td>
                            );
                          case 'is_placeholder':
                            return (
                              <td key={colId}>{isPlaceholderEvent(obj) ? 'Yes' : 'No'}</td>
                            );
                          default:
                            return null;
                        }
                      })}
                      <td>
                        <RowActionIcons>
                          {renderVolunteerOpportunityActions(obj)}
                          {renderCopyRegistrationLinkAction(obj)}
                          <button type="button" onClick={() => navigate(`/Events/${obj._id}/edit`, { state: { event: obj, eventsFilters: { searchTerm, statusFilter, filterProgramId, filterOrganizerId, timeNav: { view: timeNav.view, focusDateYmd: timeNav.focusDateYmd } }, returnTo: returnToHere } })} className="btn-link" style={iconBtnStyle} title="Edit">
                            <IconEdit size={18} />
                          </button>
                          {(obj.status || 'Open') === 'Closed' && (
                            <button type="button" onClick={() => reopenOneEvent(obj)} className="btn-link-muted" style={iconBtnStyle} title="Reopen">
                              <IconReopen size={18} />
                            </button>
                          )}
                          <button type="button" onClick={() => duplicateEvent(obj)} className="btn-link" style={iconBtnStyle} title="Duplicate">
                            <IconDuplicate size={18} />
                          </button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sortedObjs.length === 0 && <p className="empty-message">No events. Add one above.</p>}
          </>
        )}

        {viewMode === 'calendar' && (
          <EventScheduleCalendar
            items={sortedObjs}
            view={timeNav.view}
            focusDateYmd={timeNav.focusDateYmd}
            onItemClick={(obj) => setDetailEventId(obj._id)}
          />
        )}
      </div>
      {detailEventId && (
        <EventDetailModal
          eventId={detailEventId}
          open={!!detailEventId}
          onClose={() => setDetailEventId(null)}
          returnTo={returnToHere}
          eventsFilters={{ searchTerm, statusFilter, filterProgramId, filterOrganizerId, timeNav: { view: timeNav.view, focusDateYmd: timeNav.focusDateYmd } }}
        />
      )}
    </div>
  );
}
export default Events;
